import type { ReactElement } from "react";
import type {
  ExposureRiskCandidate,
  ExposureRiskReviewParams,
} from "@playbookai/widget-schemas";
import { cn } from "../../lib/utils.js";
import type { WidgetBodyProps } from "../types.js";
import {
  EXPOSURE_RISK_CATEGORY_LABELS,
  EXPOSURE_RISK_CRITICALITY_LABELS,
  EXPOSURE_RISK_CRITICALITY_TONE,
  EXPOSURE_RISK_TYPE_LABELS,
  exposureRiskRollup,
  sortExposureRisks,
} from "./presentation.js";

export const RISK_VISIBLE_LIMIT = 3;

export function RiskSummary({
  data,
}: {
  data: ExposureRiskReviewParams;
}): ReactElement {
  const rollup = exposureRiskRollup(data.risks);
  return (
    <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-pb-text-muted">
      <span>
        {rollup.candidates} candidate{rollup.candidates === 1 ? "" : "s"}
      </span>
      <span
        className={rollup.highOrCritical > 0 ? "text-pb-red-text" : undefined}
      >
        {rollup.highOrCritical} high / critical
      </span>
      <span className={rollup.unknown > 0 ? "text-pb-amber-text" : undefined}>
        {rollup.unknown} unknown
      </span>
    </p>
  );
}

/** Shared row content; ratings and categories keep their existing meaning. */
export function RiskRowSummary({
  risk,
}: {
  risk: ExposureRiskCandidate;
}): ReactElement {
  return (
    <span className="flex min-w-0 items-start gap-3">
      <span className="min-w-0 flex-1">
        <span className="block break-words text-sm font-medium text-pb-text">
          {risk.title || "Untitled risk"}
        </span>
        <span className="mt-1 block text-xs text-pb-text-muted">
          {EXPOSURE_RISK_TYPE_LABELS[risk.type]} ·{" "}
          {EXPOSURE_RISK_CATEGORY_LABELS[risk.category]}
        </span>
      </span>
      <span
        className={cn(
          "shrink-0 rounded-md border px-1.5 py-0.5 text-xs font-medium",
          EXPOSURE_RISK_CRITICALITY_TONE[risk.criticality],
        )}
      >
        {EXPOSURE_RISK_CRITICALITY_LABELS[risk.criticality]}
      </span>
    </span>
  );
}

export function ExposureRiskReviewWidget({
  data,
}: WidgetBodyProps<ExposureRiskReviewParams>): ReactElement {
  if (data.risks.length === 0)
    return (
      <div className="grid flex-1 place-items-center px-4 py-3 text-center text-xs italic text-pb-text-muted">
        No candidate exposure or containment risks yet.
      </div>
    );
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
      <RiskSummary data={data} />
      <ul
        aria-label="Candidate risks"
        className="min-h-0 flex-1 divide-y divide-pb-border overflow-auto"
      >
        {sortExposureRisks(data.risks)
          .slice(0, RISK_VISIBLE_LIMIT)
          .map((risk) => (
            <li key={risk.id} className="py-3">
              <RiskRowSummary risk={risk} />
            </li>
          ))}
      </ul>
    </div>
  );
}
